import React, { useState } from 'react';
import { Plus, Trash2, DollarSign, Receipt, FileText } from 'lucide-react';
import InfoTooltip from '../../../../components/InfoTooltip';

const Step5FeesandTaxes = ({ formData, onChange }) => {
  const [activeTab, setActiveTab] = useState('fees');

  const addFee = () => {
    const feeName = prompt('Enter fee name:');
    if (feeName) {
      onChange({
        dynamicFees: {
          ...formData.dynamicFees,
          [feeName]: { type: 'percentage', value: 0 }
        }
      });
    }
  };

  const updateFee = (feeName, updates) => {
    onChange({
      dynamicFees: {
        ...formData.dynamicFees,
        [feeName]: { ...formData.dynamicFees[feeName], ...updates }
      }
    });
  };

  const removeFee = (feeName) => {
    const newFees = { ...formData.dynamicFees };
    delete newFees[feeName];
    onChange({ dynamicFees: newFees });
  };

  const addTax = () => {
    const taxName = prompt('Enter tax name:');
    if (taxName) {
      onChange({
        dynamicTaxes: {
          ...formData.dynamicTaxes,
          [taxName]: { type: 'percentage', value: 0 }
        }
      });
    }
  };

  const updateTax = (taxName, updates) => {
    onChange({
      dynamicTaxes: {
        ...formData.dynamicTaxes,
        [taxName]: { ...formData.dynamicTaxes[taxName], ...updates }
      }
    });
  };

  const removeTax = (taxName) => {
    const newTaxes = { ...formData.dynamicTaxes };
    delete newTaxes[taxName];
    onChange({ dynamicTaxes: newTaxes });
  };

  const tabs = [
    { id: 'fees', name: 'Dynamic Fees', icon: DollarSign },
    { id: 'taxes', name: 'Dynamic Taxes', icon: Receipt }
  ];

  return (
    <div className="space-y-6">
      <div>
        <h4 className="text-lg font-medium text-gray-900 mb-2">Fees & Taxes</h4>
        <p className="text-sm text-gray-600">
          Configure dynamic fees and taxes applied to rides.
        </p>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === tab.id
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <tab.icon className="h-4 w-4 inline mr-2" />
              {tab.name}
            </button>
          ))}
        </nav>
      </div>

      {/* Dynamic Fees Tab */}
      {activeTab === 'fees' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h5 className="text-md font-medium text-gray-900">Dynamic Fees</h5>
            <button
              onClick={addFee}
              className="btn btn-sm btn-primary"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Fee
            </button>
          </div>

          <div className="space-y-4">
            {Object.entries(formData.dynamicFees || {}).map(([feeName, fee]) => (
              <div key={feeName} className="border rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <h6 className="text-sm font-medium text-gray-900">{feeName}</h6>
                  <button
                    onClick={() => removeFee(feeName)}
                    className="text-red-600 hover:text-red-800"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="form-label">
                      Type
                      <InfoTooltip text="Choose percentage for rate-based fees or fixed for flat amount fees" />
                    </label>
                    <select
                      value={fee.type}
                      onChange={(e) => updateFee(feeName, { type: e.target.value })}
                      className="input"
                    >
                      <option value="percentage">Percentage</option>
                      <option value="fixed">Fixed Amount</option>
                    </select>
                  </div>
                  <div>
                    <label className="form-label">
                      Value
                      <InfoTooltip text="Enter the fee amount. For percentage, use values like 10 for 10%. For fixed, use actual amount" />
                    </label>
                    <input
                      type="number"
                      value={fee.value}
                      onChange={(e) => updateFee(feeName, { value: parseFloat(e.target.value) || 0 })}
                      className="input"
                      step="0.01"
                      min="0"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Dynamic Taxes Tab */}
      {activeTab === 'taxes' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h5 className="text-md font-medium text-gray-900">Dynamic Taxes</h5>
            <button
              onClick={addTax}
              className="btn btn-sm btn-primary"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Tax
            </button>
          </div>

          <div className="space-y-4">
            {Object.entries(formData.dynamicTaxes || {}).map(([taxName, tax]) => (
              <div key={taxName} className="border rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <h6 className="text-sm font-medium text-gray-900">{taxName}</h6>
                  <button
                    onClick={() => removeTax(taxName)}
                    className="text-red-600 hover:text-red-800"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="form-label">
                      Type
                      <InfoTooltip text="Choose percentage for rate-based taxes or fixed for flat amount taxes" />
                    </label>
                    <select
                      value={tax.type}
                      onChange={(e) => updateTax(taxName, { type: e.target.value })}
                      className="input"
                    >
                      <option value="percentage">Percentage</option>
                      <option value="fixed">Fixed Amount</option>
                    </select>
                  </div>
                  <div>
                    <label className="form-label">
                      Value
                      <InfoTooltip text="Enter the tax amount. For percentage, use values like 5 for 5%. For fixed, use actual amount" />
                    </label>
                    <input
                      type="number"
                      value={tax.value}
                      onChange={(e) => updateTax(taxName, { value: parseFloat(e.target.value) || 0 })}
                      className="input"
                      step="0.01"
                      min="0"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Summary */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h5 className="text-sm font-medium text-blue-800 mb-2">Configuration Summary</h5>
        <div className="text-sm text-blue-700 space-y-1">
          <p>Dynamic Fees: {Object.keys(formData.dynamicFees || {}).length}</p>
          <p>Dynamic Taxes: {Object.keys(formData.dynamicTaxes || {}).length}</p>
        </div>
      </div>
    </div>
  );
};

export default Step5FeesandTaxes;
